import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";
import { Users, ClipboardCheck, Wallet, FileText, Menu, X, Building2, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";

const sections = ["home", "about", "services", "contact"];

function useActiveSection() {
  const [active, setActive] = useState("home");
  useEffect(() => {
    const onScroll = () => {
      let current = "home";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 200) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return active;
}

const NavLink = ({ id, active, children, onClick }) => (
  <a
    href={`#${id}`}
    onClick={onClick}
    data-testid={`nav-${id}`}
    className={`px-1 py-1 text-sm font-medium tracking-wide transition-colors duration-200 ${
      active === id ? "text-[#C59B27]" : "text-white/80 hover:text-white"
    }`}
  >
    {children}
  </a>
);

export default function Home() {
  const active = useActiveSection();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="min-h-screen bg-[#FDFDF9]" data-testid="home-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0F1D42] border-b border-[#0F1D42]/60">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <a href="#home" className="flex items-center gap-3" data-testid="brand-link">
            <BrandLogo alt="MRS" className="w-9 h-9 object-contain bg-white rounded" />
            <div className="text-white">
              <div className="text-xs uppercase tracking-[0.2em] text-[#C59B27] font-bold leading-none">Enterprise</div>
              <div className="font-serif text-lg leading-none mt-1">MRS Enterprise</div>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <NavLink id="home" active={active}>Home</NavLink>
            <NavLink id="about" active={active}>About Us</NavLink>
            <NavLink id="services" active={active}>Services</NavLink>
            <NavLink id="contact" active={active}>Contact Us</NavLink>
            <Link to="/login" data-testid="admin-login-button">
              <Button className="bg-[#C59B27] hover:bg-[#a8821c] text-black font-semibold transition-colors">
                Admin Login <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </nav>
          <button className="md:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <nav className="md:hidden bg-[#0F1D42] px-4 pb-4 pt-2 flex flex-col gap-3 border-t border-white/10">
            <NavLink id="home" active={active} onClick={close}>Home</NavLink>
            <NavLink id="about" active={active} onClick={close}>About Us</NavLink>
            <NavLink id="services" active={active} onClick={close}>Services</NavLink>
            <NavLink id="contact" active={active} onClick={close}>Contact Us</NavLink>
            <Link to="/login" onClick={close}>
              <Button className="bg-[#C59B27] hover:bg-[#a8821c] text-black font-semibold w-full">Admin Login</Button>
            </Link>
          </nav>
        )}
      </header>

      {/* Hero / Home */}
      <section id="home" className="relative overflow-hidden bg-[#0F1D42] text-white">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#C59B27]/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#C59B27]/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-28 relative z-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#C59B27]/15 text-[#C59B27] rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C59B27]" /> Est. Malda, West Bengal
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
              People, Payroll & <span className="text-[#C59B27]">Purpose</span> — under one roof.
            </h1>
            <p className="mt-6 text-white/70 text-base sm:text-lg max-w-xl">
              MRS Enterprise runs a modern, integrated HRMS to manage workers, attendance, wages
              and payments with precision — designed and operated for the company's own business use.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#services">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white h-11 px-5" data-testid="learn-more-button">
                  Explore Services
                </Button>
              </a>
              <Link to="/login">
                <Button className="bg-[#C59B27] hover:bg-[#a8821c] text-black font-semibold h-11 px-5" data-testid="hero-admin-button">
                  Admin Login <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md text-white/70 text-sm">
              <div><div className="font-serif text-3xl text-white">24-23</div><div>Payroll cycle</div></div>
              <div><div className="font-serif text-3xl text-white">100%</div><div>Digital records</div></div>
              <div><div className="font-serif text-3xl text-white">₹</div><div>INR payroll</div></div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] bg-gradient-to-br from-[#C59B27]/20 via-white/5 to-white/0 rounded-2xl border border-white/10 backdrop-blur-sm p-8 flex flex-col justify-between">
              <BrandLogo alt="MRS Logo" className="w-28 h-28 rounded-xl bg-white p-2 shadow-2xl" />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#C59B27] font-bold">A trusted enterprise</div>
                <div className="font-serif text-4xl mt-2">Serving workers,<br />building futures.</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white text-[#0F1D42] rounded-xl shadow-2xl p-4 max-w-xs border border-slate-200 hidden md:block">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#C59B27] font-bold">Certified &amp; Secure</div>
              <div className="text-sm mt-1 font-medium">All employee data is stored securely, only accessed by MRS Enterprise administration.</div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-14 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C59B27] font-bold">About Us</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2 leading-tight">
              An enterprise rooted in Malda,<br />built on <span className="text-[#C59B27]">trust</span> &amp; hard work.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              MRS Enterprise is a growing production &amp; labor services business in Malda, West Bengal.
              We operate with a strong sense of responsibility to our workforce — timely wages,
              transparent records, and dignified working conditions. This platform is our internal
              backbone: a place where every worker's profile, attendance, wage and payment history
              is captured and honoured.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Owner-managed operations with direct accountability",
                "Digital worker profiles with KYC &amp; bank details",
                "Transparent daily attendance and per-day wages",
                "24th–23rd payroll cycle, aligned with local practice",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#C59B27] flex-shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: t }} />
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Card num="01" title="Since" value="Malda, WB" />
            <Card num="02" title="Focus" value="Production &amp; Labour" />
            <Card num="03" title="Payroll" value="Per-day + OT" />
            <Card num="04" title="Records" value="100% Digital" />
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-white border-y border-[#E5E2DC]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C59B27] font-bold">Services</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2 leading-tight">
              A single integrated platform for<br />all our <span className="text-[#C59B27]">operations</span>.
            </h2>
            <p className="mt-4 text-slate-600">
              Every module below is built into the MRS Enterprise Admin panel — everything connected,
              nothing scattered across spreadsheets.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            <Service icon={Users} title="Employee Management"
              desc="Complete worker profiles: photo, Aadhaar, PAN, bank details, joining date &amp; wages." />
            <Service icon={ClipboardCheck} title="Attendance Tracking"
              desc="Daily &amp; monthly attendance with Present, Absent, Half Day, Leave, Weekly Off, Holiday &amp; OT." />
            <Service icon={Wallet} title="Payroll &amp; Payments"
              desc="Per-day salary + overtime, bonus, advances, deductions. Auto-payment for cycles." />
            <Service icon={FileText} title="Professional Reports"
              desc="Salary slips, payroll &amp; worker information reports in branded PDF and Excel." />
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-14 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C59B27] font-bold">Contact Us</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2 leading-tight">
              Reach out to <span className="text-[#C59B27]">MRS Enterprise</span>.
            </h2>
            <p className="mt-4 text-slate-600 max-w-md">
              For business inquiries, worker onboarding, or any operational question,
              please get in touch using the details below.
            </p>
            <div className="mt-8 space-y-4">
              <ContactRow icon={MapPin} label="Office" value="MRS Enterprise, Malda, West Bengal, India" />
              <ContactRow icon={Phone} label="Phone" value="+91 — Please contact for business inquiries" />
              <ContactRow icon={Mail} label="Email" value="mrsenterprise@example.in" />
              <ContactRow icon={Building2} label="Proprietor" value="Sattar Ali" />
            </div>
          </div>
          <div className="bg-[#0F1D42] text-white p-8 md:p-10 rounded-2xl border border-[#0F1D42] shadow-xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C59B27] font-bold">For MRS Administration</div>
            <h3 className="font-serif text-2xl mt-2">Access the admin panel</h3>
            <p className="mt-3 text-white/70 text-sm leading-relaxed">
              Sign in as an administrator to manage employees, attendance, payroll and payments.
              This portal is restricted to MRS Enterprise personnel only.
            </p>
            <Link to="/login" className="inline-block mt-6">
              <Button className="bg-[#C59B27] hover:bg-[#a8821c] text-black font-semibold h-11 px-6" data-testid="contact-admin-button">
                Go to Admin Login <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1D42] text-white/70">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo alt="MRS" className="w-8 h-8 bg-white rounded p-0.5" />
            <div>
              <div className="text-white font-serif">MRS Enterprise</div>
              <div className="text-xs">Malda, West Bengal, India</div>
            </div>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} MRS Enterprise. All rights reserved.</div>
        </div>
        <div className="h-1 bg-[#C59B27]" />
      </footer>
    </div>
  );
}

const Card = ({ num, title, value }) => (
  <div className="border border-[#E5E2DC] bg-white rounded-lg p-5 hover:shadow-md transition-shadow duration-200">
    <div className="text-[10px] uppercase tracking-[0.15em] text-[#C59B27] font-bold">{num}</div>
    <div className="mt-6">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{title}</div>
      <div className="font-serif text-xl text-slate-900 mt-1" dangerouslySetInnerHTML={{ __html: value }} />
    </div>
  </div>
);

const Service = ({ icon: Icon, title, desc }) => (
  <div className="bg-[#FDFDF9] border border-[#E5E2DC] rounded-lg p-6 hover:border-[#C59B27] transition-colors duration-200 group">
    <div className="w-11 h-11 rounded-md bg-[#0F1D42] group-hover:bg-[#C59B27] flex items-center justify-center transition-colors duration-200">
      <Icon className="w-5 h-5 text-white" />
    </div>
    <h3 className="font-serif text-xl text-slate-900 mt-5">{title}</h3>
    <p className="text-sm text-slate-600 mt-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: desc }} />
  </div>
);

const ContactRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-md border border-[#E5E2DC] bg-white flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#C59B27]" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="text-slate-900 mt-1">{value}</div>
    </div>
  </div>
);
